import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, CreditCard, Globe, Shield, Zap, Users, Star, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchServices();
    fetchTestimonials();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`);
      setServices(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Code: Code,
      CreditCard: CreditCard,
      Globe: Globe,
      Shield: Shield,
      Zap: Zap,
      Palette: Code,
      Link: Globe,
      Cloud: Globe,
      MessageSquare: Code,
      Building: Globe,
      Wrench: Zap,
    };
    const IconComponent = icons[iconName] || Code;
    return <IconComponent size={24} />;
  };

  const whyChooseUs = [
    {
      icon: <Shield size={32} />,
      title: 'Reliability',
      description: 'We deliver robust solutions that stand the test of time with 99.9% uptime guarantee.'
    },
    {
      icon: <Zap size={32} />,
      title: 'Innovation',
      description: 'Cutting-edge technologies and creative approaches to solve complex business challenges.'
    },
    {
      icon: <Users size={32} />,
      title: 'Scalability',
      description: 'Build once, scale infinitely. Our solutions grow with your business needs.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1771873680097-7506773a2b02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxkYXJrJTIwdGVjaCUyMGFic3RyYWN0JTIwYmFja2dyb3VuZCUyMG9yYW5nZSUyMGJsdWV8ZW58MHx8fHwxNzc0NjI5NDE3fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-6 animate-fade-up">
              Software &bull; Fintech &bull; Automation
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6 animate-fade-up stagger-1">
              Building Smart Digital Solutions for{' '}
              <span className="text-[#FF5E1A]">Modern Businesses</span>
            </h1>
            <p className="text-lg text-[#A1B0CC] leading-relaxed mb-8 animate-fade-up stagger-2">
              Mohtech Cyber Solutions helps businesses grow by building reliable digital systems, 
              with strong specialization in payment integrations and automation solutions.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-up stagger-3">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-bold rounded-full px-8 py-4 flex items-center gap-2 transition-all duration-300 btn-glow"
              >
                Get Started
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="bg-transparent text-white border border-white/20 hover:border-white/60 rounded-full px-8 py-4 font-semibold transition-all duration-300"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-section" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              What We Do
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
              Our Core Services
            </h2>
            <p className="text-[#A1B0CC] max-w-2xl mx-auto">
              From custom software development to fintech solutions, we provide comprehensive 
              digital services to transform your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={service.id}
                data-testid={`service-card-${index}`}
                className="bg-[#0A1121] border border-white/5 rounded-2xl p-8 card-hover"
              >
                <div className="w-12 h-12 bg-[#FF5E1A]/10 rounded-xl flex items-center justify-center text-[#FF5E1A] mb-6">
                  {getIcon(service.icon)}
                </div>
                <h3 className="text-white font-semibold text-xl mb-3">{service.title}</h3>
                <p className="text-[#A1B0CC] text-sm leading-relaxed mb-4">{service.description}</p>
                <Link
                  to="/services"
                  className="text-[#FF5E1A] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
                >
                  Learn more <ChevronRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="view-all-services"
              className="inline-flex items-center gap-2 text-[#FF5E1A] font-semibold hover:gap-3 transition-all"
            >
              View All Services <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-us" className="py-24 bg-[#0A1121]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
                Your Trusted Technology Partner
              </h2>
              <p className="text-[#A1B0CC] leading-relaxed mb-8">
                Based in Nairobi, Kenya, we understand the unique challenges and opportunities 
                of the African market. Our team combines global expertise with local insights 
                to deliver solutions that truly work for your business.
              </p>
              <Link
                to="/about"
                data-testid="learn-more-about"
                className="inline-flex items-center gap-2 bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-bold rounded-full px-6 py-3 transition-all btn-glow"
              >
                Learn More About Us <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid gap-6">
              {whyChooseUs.map((item, index) => (
                <div
                  key={index}
                  data-testid={`why-card-${index}`}
                  className="bg-[#050B14] border border-white/5 rounded-2xl p-6 flex gap-6 card-hover"
                >
                  <div className="w-16 h-16 bg-[#FF5E1A]/10 rounded-xl flex items-center justify-center text-[#FF5E1A] flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-[#A1B0CC] text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Testimonials
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                data-testid={`testimonial-card-${index}`}
                className="testimonial-card border border-white/5 rounded-2xl p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#FF5E1A] text-[#FF5E1A]" />
                  ))}
                </div>
                <p className="text-[#A1B0CC] leading-relaxed mb-6 text-sm">
                  &ldquo;{testimonial.content}&rdquo;
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FF5E1A]/20 rounded-full flex items-center justify-center">
                    <span className="text-[#FF5E1A] font-bold">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.name}</h4>
                    <p className="text-[#5B6B8A] text-sm">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-[#0A1121]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-[#FF5E1A]/20 to-transparent border border-[#FF5E1A]/20 rounded-3xl p-12 lg:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-[#A1B0CC] max-w-2xl mx-auto mb-8">
              Let&apos;s discuss how we can help you build reliable digital solutions 
              that drive growth and efficiency.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-2 bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-bold rounded-full px-8 py-4 transition-all btn-glow"
            >
              Contact Us Today <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
